// ---------------------------------------------------------------------------
// Client/server clock offset
//
// The session transcript sorts queued rows last; other rows use
// `processing_started_at ?? authored_at ?? created_at`. The server stamps rows in its own
// clock domain when a turn is claimed. Idle optimistic messages use a local
// created_at, while force-sent pairs use a provisional processing stamp. If
// the client's wall clock runs ahead of the server, raw client timestamps can
// sort an optimistic message after its own streaming reply until the server
// claim timestamp lands (BLA-4339).
//
// To avoid that, optimistic timestamps use the server's time domain.
// Responses carry a fresh `server_time_ms`; comparing it against the client
// clock at receipt yields an offset we apply to `Date.now()` at send time.
//
// The estimate is intentionally biased safe: network latency makes the
// observed offset slightly *more negative* than the true clock skew, so the
// optimistic timestamp lands a touch earlier than real server-now, keeping
// the message ahead of the reply the server stamps a moment later.
// ---------------------------------------------------------------------------

const MAX_PLAUSIBLE_OFFSET_MS = 24 * 60 * 60 * 1000;

let offsetMs = 0;
let hasSample = false;

export function recordServerTimeMs(
  serverTimeMs: number | null | undefined,
): void {
  if (serverTimeMs == null || !Number.isFinite(serverTimeMs)) return;
  const observed = serverTimeMs - Date.now();
  // Reject garbage (wrong units, unsynced/broken clock) rather than let a
  // single bad sample poison every optimistic timestamp.
  if (Math.abs(observed) > MAX_PLAUSIBLE_OFFSET_MS) return;
  offsetMs = observed;
  hasSample = true;
}

/** Best estimate of the server's current epoch ms. */
export function serverAdjustedNow(): number {
  return Date.now() + offsetMs;
}

/** Server-adjusted now as an ISO string, for stamping optimistic rows. */
export function serverAdjustedNowIso(): string {
  return new Date(serverAdjustedNow()).toISOString();
}

/** Current offset estimate in ms (server minus client). */
export function getServerClockOffsetMs(): number {
  return offsetMs;
}

/** A server-stamped epoch ms mapped onto the client clock, for `setTimeout`-driven countdowns. */
export function serverTimeToLocalMs(serverTimeMs: number): number {
  return serverTimeMs - offsetMs;
}

/** Test-only: clear the recorded offset. */
export function __resetServerClockForTests(): void {
  offsetMs = 0;
  hasSample = false;
}

/** Test-only: whether any sample has been recorded. */
export function __hasServerClockSampleForTests(): boolean {
  return hasSample;
}
